/**
 * KPIBouquetsStrip — Bandeau de 5 bouquets KPI contextuels (P3).
 * Charge /api/cockpit/bouquets et affiche 3-5 KPIs par bouquet, avec
 * delta % vs mois précédent quand pertinent et lien contextuel.
 *
 * Props :
 *   - scope : 'mine' | 'agency' (défaut 'mine')
 *   - variant : 'agent' | 'admin' (colorimétrie)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    TrendingUp, Briefcase, ShieldCheck, Euro, FolderClosed,
    ArrowRight, ArrowUpRight, ArrowDownRight, Minus, Star,
    Settings2, FileDown, Check, X,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const ICON_MAP = {
    'trending-up': TrendingUp,
    'briefcase': Briefcase,
    'shield-check': ShieldCheck,
    'euro': Euro,
    'folder-closed': FolderClosed,
};

const CURRENCY = new Intl.NumberFormat('fr-FR',
    { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

function formatKPIValue(kpi) {
    if (kpi.value == null || kpi.value === '') return '—';
    switch (kpi.format) {
        case 'currency': return CURRENCY.format(kpi.value);
        case 'percent': return `${kpi.value}%`;
        case 'rating':
            return typeof kpi.value === 'number' ? kpi.value.toFixed(1) : kpi.value;
        case 'days': return `${kpi.value} j`;
        default: return Intl.NumberFormat('fr-FR').format(kpi.value);
    }
}

function DeltaBadge({ percent, label }) {
    if (percent == null) return null;
    const up = percent > 0, down = percent < 0;
    const Icon = up ? ArrowUpRight : (down ? ArrowDownRight : Minus);
    const color = up ? '#16a34a' : (down ? '#dc2626' : '#94a3b8');
    return (
        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold"
              data-testid="kpi-delta"
              style={{ color }}>
            <Icon className="w-3 h-3" />
            {Math.abs(percent)}%
            {label && <span className="ml-0.5 text-muted-foreground font-normal">· {label}</span>}
        </span>
    );
}

function BouquetCard({ bouquet, variant }) {
    const Icon = ICON_MAP[bouquet.icon] || TrendingUp;
    const isViolet = variant === 'admin';
    const accent = isViolet ? '#681793' : '#FF914D';
    return (
        <article
            data-testid={`bouquet-${bouquet.code}`}
            className="rounded-2xl p-4 border transition hover:shadow-sm"
            style={{
                background: 'var(--card, #ffffff)',
                borderColor: 'rgba(104,23,147,0.15)',
            }}
        >
            <header className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                         style={{ background: `${accent}1A`, color: accent }}>
                        <Icon className="w-4 h-4" />
                    </div>
                    <h3 className="text-sm font-bold text-foreground">{bouquet.title}</h3>
                </div>
                {bouquet.cta && (
                    <Link to={bouquet.cta.route}
                          data-testid={`bouquet-${bouquet.code}-cta`}
                          className="inline-flex items-center gap-0.5 text-[11px] font-semibold hover:underline"
                          style={{ color: accent }}>
                        {bouquet.cta.label}
                        <ArrowRight className="w-3 h-3" />
                    </Link>
                )}
            </header>
            <div className="grid grid-cols-2 gap-2">
                {(bouquet.kpis || []).slice(0, 4).map((kpi) => (
                    <div key={kpi.code}
                         data-testid={`bouquet-${bouquet.code}-kpi-${kpi.code}`}
                         className="min-w-0"
                         title={kpi.hint || ''}>
                        <p className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground truncate">
                            {kpi.label}
                        </p>
                        <div className="flex items-baseline gap-1 mt-0.5">
                            <p className="text-lg font-extrabold tabular-nums text-foreground truncate">
                                {formatKPIValue(kpi)}
                            </p>
                            {kpi.format === 'rating' && kpi.value != null && (
                                <Star className="w-3 h-3" style={{ color: accent, fill: accent }} />
                            )}
                        </div>
                        {kpi.delta_percent != null && (
                            <DeltaBadge percent={kpi.delta_percent} label={kpi.delta_label} />
                        )}
                    </div>
                ))}
            </div>
        </article>
    );
}

export default function KPIBouquetsStrip({ scope = 'mine', variant = 'agent' }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [configOpen, setConfigOpen] = useState(false);
    const [savingPrefs, setSavingPrefs] = useState(false);
    const [exportingPdf, setExportingPdf] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(`/cockpit/bouquets?scope=${scope}`);
            setData(r.data);
        } catch (e) {
            setError(e?.response?.status === 403 ? 'Accès refusé' : 'Impossible de charger les bouquets');
        } finally { setLoading(false); }
    }, [scope]);

    useEffect(() => { load(); }, [load]);

    const toggleBouquet = async (code) => {
        if (!data) return;
        const currentHidden = new Set(data.hidden_codes || []);
        if (currentHidden.has(code)) currentHidden.delete(code);
        else currentHidden.add(code);
        // Empêche de tout masquer
        if (currentHidden.size >= 5) {
            toast.error('Au moins un bouquet doit rester visible');
            return;
        }
        setSavingPrefs(true);
        try {
            await api.patch('/cockpit/bouquets/preferences',
                             { hidden_codes: Array.from(currentHidden) });
            await load();
        } catch (e) { toast.error('Enregistrement impossible'); }
        finally { setSavingPrefs(false); }
    };

    const exportPdf = async () => {
        setExportingPdf(true);
        try {
            const r = await api.get(`/cockpit/bouquets/pdf?scope=${scope}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(r.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `cockpit-homly-${scope}-${new Date().toISOString().slice(0, 10)}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Cockpit exporté en PDF');

            // P2 : trigger Premium au 3ᵉ téléchargement PDF cockpit.
            const trigger = r.headers?.['x-premium-trigger']
                || r.headers?.get?.('x-premium-trigger');
            if (trigger === 'pdf_downloads_3') {
                toast('Automatisez ce reporting', {
                    description: 'Le lundi matin, recevez votre cockpit PDF par email — pour 19 € HT / mois.',
                    duration: 12000,
                    action: {
                        label: 'En savoir plus',
                        onClick: () => { window.location.href = '/reseau/parametres?source=pdf_trigger'; },
                    },
                });
            }
        } catch (e) { toast.error('Export PDF impossible'); }
        finally { setExportingPdf(false); }
    };

    if (loading) {
        return (
            <section data-testid="bouquets-loading"
                     className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[0, 1, 2, 3, 4].map((i) => (
                    <div key={i} className="rounded-2xl p-4 border bg-card animate-pulse h-36"
                         style={{ borderColor: 'rgba(104,23,147,0.15)' }} />
                ))}
            </section>
        );
    }
    if (error) {
        return (
            <div data-testid="bouquets-error" className="text-xs text-muted-foreground">
                {error}
            </div>
        );
    }
    if (!data?.bouquets) return null;

    const accent = variant === 'admin' ? '#681793' : '#FF914D';
    const allCodes = data.all_codes || ['activity', 'portfolio', 'reputation', 'money', 'notary'];
    const hiddenSet = new Set(data.hidden_codes || []);
    const BOUQUET_LABELS = {
        activity: 'Activité du mois',
        portfolio: 'Portefeuille',
        reputation: 'Réputation',
        money: 'Argent',
        notary: 'Notaire',
    };

    return (
        <div data-testid="kpi-bouquets-container">
            <div className="flex items-center justify-end gap-2 mb-2">
                <button type="button" onClick={exportPdf} disabled={exportingPdf}
                        data-testid="bouquets-export-pdf"
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border transition hover:bg-muted"
                        style={{ borderColor: 'rgba(0,0,0,0.15)', color: 'var(--foreground)' }}>
                    <FileDown className="w-3.5 h-3.5" />
                    {exportingPdf ? 'Export…' : 'PDF'}
                </button>
                <div className="relative">
                    <button type="button" onClick={() => setConfigOpen(!configOpen)}
                            data-testid="bouquets-config-btn"
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border transition hover:bg-muted"
                            style={{ borderColor: 'rgba(0,0,0,0.15)', color: 'var(--foreground)' }}>
                        <Settings2 className="w-3.5 h-3.5" />
                        Configurer
                    </button>
                    {configOpen && (
                        <div data-testid="bouquets-config-menu"
                             className="absolute right-0 top-full mt-1 z-50 w-64 rounded-xl border shadow-lg p-2"
                             style={{ background: 'var(--card, #ffffff)',
                                       borderColor: 'rgba(0,0,0,0.10)' }}>
                            <div className="flex items-center justify-between px-2 py-1">
                                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                                    Bouquets visibles
                                </p>
                                <button type="button" onClick={() => setConfigOpen(false)}
                                        aria-label="Fermer"
                                        className="p-1"><X className="w-3 h-3" /></button>
                            </div>
                            {allCodes.map((code) => {
                                const visible = !hiddenSet.has(code);
                                return (
                                    <button key={code} type="button"
                                            onClick={() => toggleBouquet(code)}
                                            disabled={savingPrefs}
                                            data-testid={`bouquets-toggle-${code}`}
                                            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm hover:bg-muted transition text-left">
                                        <span className="w-4 h-4 rounded flex items-center justify-center border"
                                              style={{
                                                  borderColor: visible ? accent : 'rgba(0,0,0,0.25)',
                                                  background: visible ? accent : 'transparent',
                                              }}>
                                            {visible && <Check className="w-3 h-3 text-white" />}
                                        </span>
                                        <span style={{ color: 'var(--foreground)' }}>
                                            {BOUQUET_LABELS[code] || code}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
            <section data-testid="kpi-bouquets"
                     className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.bouquets.map((b) => (
                    <BouquetCard key={b.code} bouquet={b} variant={variant} />
                ))}
            </section>
        </div>
    );
}
